import React from "react";
import { EXTRACT_COLUMNS } from "./controller";
import {
    ActionButton,
    PanelShell,
    SearchInput,
    StatPill,
} from "./ReusableComponents";

function Stepper({ active }) {
    const steps = [
        { key: "setup", label: "Setup" },
        { key: "review", label: "Review" },
        { key: "zip", label: "ZIP" },
        { key: "saving", label: "Save" },
        { key: "complete", label: "Done" },
    ];

    const activeIndex = steps.findIndex((step) => step.key === active);

    return (
        <div className="flex flex-wrap items-center justify-center gap-2">
            {steps.map((step, index) => {
                const done = index < activeIndex;
                const current = index === activeIndex;

                return (
                    <div
                        key={step.key}
                        className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${
                            current
                                ? "border-[#01C37D] bg-[#01C37D]/10 text-[#007a4e]"
                                : done
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-gray-200 bg-white text-gray-400"
                        }`}
                    >
                        <span
                            className={`grid h-5 w-5 place-items-center rounded-full text-[11px] ${
                                current || done
                                    ? "bg-[#01C37D] text-white"
                                    : "bg-gray-200 text-gray-500"
                            }`}
                        >
                            {done ? "✓" : index + 1}
                        </span>
                        {step.label}
                    </div>
                );
            })}
        </div>
    );
}

function PageShell({ children, activeStep = "setup" }) {
    return (
        <div className="min-h-[calc(100vh-180px)] bg-gray-50 px-4 py-5">
            <Stepper active={activeStep} />
            {children}
        </div>
    );
}

function UomSelector({ controller }) {
    const {
        uomPanelRef,
        uomLoading,
        extracting,
        saving,
        showUomPanel,
        setShowUomPanel,
        selectedUomCodes,
        uomOptions,
        toggleUomSelection,
        clearSelectedUoms,
    } = controller;

    return (
        <div className="relative" ref={uomPanelRef}>
            <button
                type="button"
                className="h-9 min-w-[150px] rounded-lg border bg-white px-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                disabled={uomLoading || extracting || saving}
                onClick={() => setShowUomPanel((prev) => !prev)}
            >
                {uomLoading
                    ? "Loading..."
                    : selectedUomCodes.length
                        ? `UOM: ${selectedUomCodes.length}`
                        : "Select UOM"}
            </button>

            {showUomPanel && (
                <PanelShell className="w-[320px]">
                    <div className="mb-2 flex items-center justify-between gap-3">
                        <div>
                            <div className="text-sm font-semibold text-gray-800">
                                Select UOM
                            </div>
                            <div className="text-xs text-gray-500">
                                Selected UOM sizes become variant options.
                            </div>
                        </div>

                        <button
                            type="button"
                            className="rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
                            onClick={() => setShowUomPanel(false)}
                        >
                            Close
                        </button>
                    </div>

                    <div className="max-h-[220px] overflow-auto rounded-lg border">
                        {uomOptions.length === 0 ? (
                            <div className="px-3 py-6 text-center text-xs text-gray-400">
                                No UOM found.
                            </div>
                        ) : (
                            uomOptions.map((uom) => {
                                const checked = selectedUomCodes.includes(
                                    uom.uom_code,
                                );

                                return (
                                    <label
                                        key={uom.uom_code}
                                        className="flex cursor-pointer items-center justify-between border-b px-3 py-2 last:border-b-0 hover:bg-gray-50"
                                    >
                                        <div>
                                            <div className="text-xs font-medium text-gray-800">
                                                {uom.uom_name}
                                            </div>
                                            <div className="text-[10px] text-gray-400">
                                                {uom.uom_code}
                                            </div>
                                        </div>

                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() =>
                                                toggleUomSelection(uom.uom_code)
                                            }
                                        />
                                    </label>
                                );
                            })
                        )}
                    </div>

                    <div className="mt-2 flex justify-between gap-2">
                        <button
                            type="button"
                            className="rounded-lg border px-2.5 py-1 text-xs hover:bg-gray-50"
                            onClick={clearSelectedUoms}
                        >
                            Clear
                        </button>

                        <button
                            type="button"
                            className="rounded-lg bg-[#01C37D] px-3 py-1 text-xs font-semibold text-white hover:bg-[#00a96c]"
                            onClick={() => setShowUomPanel(false)}
                        >
                            Done
                        </button>
                    </div>
                </PanelShell>
            )}
        </div>
    );
}

function ActiveUomCompact({ controller }) {
    const {
        selectedUomCodes,
        selectedUomLabels,
        sizeLoading,
        variantOptions,
        defaultVariantValue,
        defaultVariantOption,
        applyDefaultVariantToAllRows,
    } = controller;

    if (!selectedUomCodes.length) return null;

    return (
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold text-gray-500">
                Active:
            </span>

            {selectedUomLabels.slice(0, 3).map((label) => (
                <span
                    key={label}
                    className="max-w-[130px] truncate rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
                    title={label}
                >
                    {label}
                </span>
            ))}

            {selectedUomLabels.length > 3 && (
                <span className="rounded-full border bg-white px-2 py-0.5 text-[11px] text-gray-500">
                    +{selectedUomLabels.length - 3}
                </span>
            )}

            {sizeLoading ? (
                <span className="text-[11px] text-gray-400">
                    Loading sizes...
                </span>
            ) : (
                <span className="text-[11px] text-gray-400">
                    {variantOptions.length} variants
                </span>
            )}

            {defaultVariantValue && (
                <>
                    <span
                        className="max-w-[160px] truncate rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700"
                        title={`${defaultVariantValue} / ID ${defaultVariantOption?.sizeId}`}
                    >
                        Default: {defaultVariantValue}
                    </span>

                    <button
                        type="button"
                        className="rounded-lg border bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                        onClick={applyDefaultVariantToAllRows}
                    >
                        Apply All
                    </button>
                </>
            )}
        </div>
    );
}

function ColumnSelector({ controller }) {
    const {
        columnPanelRef,
        showColumnPanel,
        setShowColumnPanel,
        columnSearchText,
        setColumnSearchText,
        filteredColumnOptions,
        visibleColumnMap,
        toggleColumnVisibility,
        showAllColumns,
        showEssentialColumns,
        resetView,
    } = controller;

    return (
        <div className="relative" ref={columnPanelRef}>
            <ActionButton onClick={() => setShowColumnPanel((prev) => !prev)}>
                Columns
            </ActionButton>

            {showColumnPanel && (
                <PanelShell className="w-[390px]">
                    <div className="mb-2 flex items-center justify-between">
                        <div>
                            <div className="text-sm font-semibold text-gray-800">
                                Table Columns
                            </div>
                            <div className="text-xs text-gray-500">
                                Choose which fields should be visible.
                            </div>
                        </div>

                        <button
                            type="button"
                            className="rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
                            onClick={() => setShowColumnPanel(false)}
                        >
                            Close
                        </button>
                    </div>

                    <input
                        className="mb-2 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:border-[#01C37D]"
                        placeholder="Search columns..."
                        value={columnSearchText}
                        onChange={(e) => setColumnSearchText(e.target.value)}
                    />

                    <div className="mb-2 flex flex-wrap gap-2">
                        <button
                            type="button"
                            className="rounded-lg border px-2.5 py-1 text-xs hover:bg-gray-50"
                            onClick={showAllColumns}
                        >
                            Show all
                        </button>

                        <button
                            type="button"
                            className="rounded-lg border px-2.5 py-1 text-xs hover:bg-gray-50"
                            onClick={showEssentialColumns}
                        >
                            Essential
                        </button>

                        <button
                            type="button"
                            className="rounded-lg border px-2.5 py-1 text-xs hover:bg-gray-50"
                            onClick={resetView}
                        >
                            Reset
                        </button>
                    </div>

                    <div className="max-h-[300px] overflow-auto rounded-lg border">
                        {filteredColumnOptions.map((column) => {
                            const checked = visibleColumnMap[column] !== false;

                            return (
                                <label
                                    key={column}
                                    className="flex cursor-pointer items-center justify-between border-b px-3 py-2 last:border-b-0 hover:bg-gray-50"
                                >
                                    <div className="min-w-0">
                                        <div className="truncate text-xs font-medium text-gray-800">
                                            {column}
                                        </div>
                                        <div className="text-[10px] text-gray-400">
                                            editable field
                                        </div>
                                    </div>

                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() =>
                                            toggleColumnVisibility(column)
                                        }
                                    />
                                </label>
                            );
                        })}
                    </div>
                </PanelShell>
            )}
        </div>
    );
}

export function ExtractSetupStep({ controller }) {
    const {
        userID,
        fileName,
        setFileName,
        selectedImage,
        extracting,
        saving,
        fileInputRef,
        handleExtractDone,
    } = controller;

    return (
        <PageShell activeStep="setup">
            <div className="mx-auto mt-6 max-w-2xl rounded-2xl border bg-white p-6 shadow-sm">
                <div className="text-center">
                    <div className="text-lg font-bold text-gray-900">
                        Start Catalogue Extraction
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                        Enter the ZIP folder name, select menu image, then extract.
                    </div>
                </div>

                <div className="mt-5 rounded-xl border bg-gray-50 p-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-600">
                                ZIP folder name / fileName
                            </label>
                            <input
                                className="h-11 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-[#01C37D]"
                                placeholder="Example: uuh"
                                value={fileName}
                                disabled={extracting || saving}
                                onChange={(e) => setFileName(e.target.value)}
                            />
                            <p className="mt-1 text-[11px] text-gray-400">
                                This same name goes to extract API and ZIP upload folder_name.
                            </p>
                        </div>

                        <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-600">
                                Menu image
                            </label>

                            <button
                                type="button"
                                className="flex h-11 w-full items-center justify-between rounded-lg border bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                disabled={extracting || saving}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <span className="truncate">
                                    {selectedImage ? selectedImage.name : "Select image"}
                                </span>
                                <span className="text-xs text-gray-400">
                                    Browse
                                </span>
                            </button>

                            <p className="mt-1 text-[11px] text-gray-400">
                                Upload menu/catalogue image for extraction.
                            </p>
                        </div>
                    </div>

                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                        <StatPill>
                            Client: {userID || "Not selected"}
                        </StatPill>

                        <ActionButton
                            variant="primary"
                            className="h-11 px-6"
                            disabled={
                                extracting ||
                                saving ||
                                !fileName.trim() ||
                                !selectedImage
                            }
                            onClick={handleExtractDone}
                        >
                            Extract Catalogue
                        </ActionButton>
                    </div>
                </div>
            </div>
        </PageShell>
    );
}

export function ExtractingStep() {
    return (
        <PageShell activeStep="setup">
            <div className="mx-auto mt-10 max-w-xl rounded-2xl border bg-white p-8 text-center shadow-sm">
                <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-[#01C37D]" />

                <div className="mt-5 text-lg font-bold text-gray-900">
                    Extracting catalogue data...
                </div>

                <div className="mt-2 text-sm text-gray-500">
                    Please wait. We are reading your menu image and preparing editable rows.
                </div>
            </div>
        </PageShell>
    );
}

export function ExtractReviewTopSection({ controller }) {
    const {
        userID,
        filteredRows,
        rows,
        visibleColumns,
        pendingCount,
        saving,
        extracting,
        selectedImage,
        fileInputRef,
        excelInputRef,
        handleExtractDone,
        handleDownloadExcel,
        handleDownloadSampleExcel,
        handleAddManualRow,
        searchText,
        setSearchText,
        setSelectedCell,
        setEditingCell,
        resetView,
        goToZipStep,
        handleClear,
    } = controller;

    return (
        <div className="border-b bg-gray-50 px-3 py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <div className="text-sm font-semibold text-gray-900">
                        Review Extracted Catalogue
                    </div>
                    <div className="text-[11px] text-gray-500">
                        Add more images, import Excel, edit cells, then continue to ZIP upload.
                    </div>
                </div>

                <Stepper active="review" />

                <div className="flex flex-wrap items-center gap-1.5">
                    <StatPill>Client: {userID || "Not selected"}</StatPill>
                    <StatPill>
                        Rows: {filteredRows.length}/{rows.length}
                    </StatPill>
                    <StatPill>
                        Cols: {visibleColumns.length}/{EXTRACT_COLUMNS.length}
                    </StatPill>

                    {pendingCount > 0 && (
                        <StatPill tone="warning">
                            Edited: {pendingCount}
                        </StatPill>
                    )}

                    {selectedImage && (
                        <StatPill tone="info" className="max-w-[180px] truncate">
                            Image: {selectedImage.name}
                        </StatPill>
                    )}
                </div>
            </div>

            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <UomSelector controller={controller} />

                    <ActionButton
                        disabled={extracting || saving}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {selectedImage ? "Change Image" : "Add Image"}
                    </ActionButton>

                    <ActionButton
                        variant="primary"
                        disabled={extracting || saving || !selectedImage}
                        onClick={handleExtractDone}
                    >
                        {extracting ? "Extracting..." : "Extract & Add"}
                    </ActionButton>

                    <ActionButton
                        disabled={saving}
                        onClick={() => excelInputRef.current?.click()}
                    >
                        Import Excel
                    </ActionButton>

                    <ActionButton
                        disabled={saving}
                        onClick={handleDownloadSampleExcel}
                    >
                        Sample Excel
                    </ActionButton>

                    <ActionButton
                        disabled={saving}
                        onClick={handleAddManualRow}
                    >
                        Add Row
                    </ActionButton>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <ActiveUomCompact controller={controller} />

                    <ActionButton
                        variant="blue"
                        disabled={!rows.length || saving}
                        onClick={goToZipStep}
                    >
                        Save & Continue
                    </ActionButton>

                    <ActionButton
                        variant="danger"
                        disabled={saving}
                        onClick={handleClear}
                    >
                        Clear
                    </ActionButton>
                </div>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2">
                <SearchInput
                    placeholder="Search rows..."
                    value={searchText}
                    onChange={(value) => {
                        setSearchText(value);
                        setSelectedCell(null);
                        setEditingCell(null);
                    }}
                    onClear={() => {
                        setSearchText("");
                        setSelectedCell(null);
                        setEditingCell(null);
                    }}
                />

                <ColumnSelector controller={controller} />

                <ActionButton
                    disabled={!rows.length || saving}
                    onClick={handleDownloadExcel}
                >
                    Download Excel
                </ActionButton>

                <ActionButton onClick={resetView}>
                    Reset
                </ActionButton>
            </div>
        </div>
    );
}

export function ExtractZipStep({ controller }) {
    const {
        fileName,
        selectedZipFile,
        zipInputRef,
        zipBaseName,
        zipNameMatches,
        rows,
        handleFinalSave,
        saving,
        handleBackToReview,
        handleClear,
    } = controller;

    return (
        <PageShell activeStep="zip">
            <div className="mx-auto mt-6 max-w-2xl rounded-2xl border bg-white p-6 shadow-sm">
                <div className="text-center">
                    <div className="text-lg font-bold text-gray-900">
                        Upload ZIP Folder
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                        ZIP file name should match the folder name used in extraction.
                    </div>
                </div>

                <div className="mt-5 rounded-xl border bg-gray-50 p-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="rounded-lg border bg-white p-3">
                            <div className="text-xs font-semibold text-gray-500">
                                Folder name
                            </div>
                            <div className="mt-1 truncate text-sm font-bold text-gray-900">
                                {fileName || "—"}
                            </div>
                        </div>

                        <div className="rounded-lg border bg-white p-3">
                            <div className="text-xs font-semibold text-gray-500">
                                Rows ready
                            </div>
                            <div className="mt-1 text-sm font-bold text-gray-900">
                                {rows.length}
                            </div>
                        </div>
                    </div>

                    <div className="mt-4">
                        <label className="mb-1 block text-xs font-semibold text-gray-600">
                            ZIP file
                        </label>

                        <button
                            type="button"
                            className="flex h-11 w-full items-center justify-between rounded-lg border bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            disabled={saving}
                            onClick={() => zipInputRef.current?.click()}
                        >
                            <span className="truncate">
                                {selectedZipFile ? selectedZipFile.name : "Select ZIP file"}
                            </span>
                            <span className="text-xs text-gray-400">
                                Browse
                            </span>
                        </button>
                    </div>

                    {selectedZipFile && (
                        <div
                            className={`mt-3 rounded-lg border px-3 py-2 text-sm ${
                                zipNameMatches
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-red-200 bg-red-50 text-red-700"
                            }`}
                        >
                            {zipNameMatches
                                ? `Name matched: ${zipBaseName}`
                                : `Name mismatch. Folder is "${fileName}" but ZIP is "${zipBaseName}".`}
                        </div>
                    )}

                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex gap-2">
                            <ActionButton onClick={handleBackToReview}>
                                Back to Table
                            </ActionButton>

                            <ActionButton variant="danger" onClick={handleClear}>
                                Cancel
                            </ActionButton>
                        </div>

                        <ActionButton
                            variant="blue"
                            className="h-11 px-6"
                            disabled={saving || !selectedZipFile || !zipNameMatches}
                            onClick={handleFinalSave}
                        >
                            Upload ZIP & Save Items
                        </ActionButton>
                    </div>
                </div>
            </div>
        </PageShell>
    );
}

export function ExtractSavingStep({ controller }) {
    const { saveStage } = controller;

    const label =
        saveStage === "uploading_zip"
            ? "Uploading ZIP..."
            : saveStage === "saving_items"
                ? "Saving catalogue items..."
                : "Saving...";

    const desc =
        saveStage === "uploading_zip"
            ? "Your ZIP is being uploaded and extracted."
            : saveStage === "saving_items"
                ? "Catalogue items are being inserted into admin."
                : "Please wait.";

    return (
        <PageShell activeStep="saving">
            <div className="mx-auto mt-10 max-w-xl rounded-2xl border bg-white p-8 text-center shadow-sm">
                <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />

                <div className="mt-5 text-lg font-bold text-gray-900">
                    {label}
                </div>

                <div className="mt-2 text-sm text-gray-500">
                    {desc}
                </div>
            </div>
        </PageShell>
    );
}

export function ExtractCompleteStep({ controller }) {
    const {
        rows,
        fileName,
        handleStartNew,
        handleDownloadExcel,
    } = controller;

    return (
        <PageShell activeStep="complete">
            <div className="mx-auto mt-10 max-w-xl rounded-2xl border bg-white p-8 text-center shadow-sm">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-100 text-2xl text-emerald-700">
                    ✓
                </div>

                <div className="mt-5 text-lg font-bold text-gray-900">
                    Upload Complete
                </div>

                <div className="mt-2 text-sm text-gray-500">
                    {rows.length} catalogue items uploaded successfully for folder{" "}
                    <span className="font-semibold text-gray-800">
                        {fileName}
                    </span>.
                </div>

                <div className="mt-6 flex flex-wrap justify-center gap-2">
                    <ActionButton onClick={handleDownloadExcel}>
                        Download Excel
                    </ActionButton>

                    <ActionButton variant="primary" onClick={handleStartNew}>
                        Start New Upload
                    </ActionButton>
                </div>
            </div>
        </PageShell>
    );
}

export default ExtractReviewTopSection;